/**
 * Image utilities for cropping and converting images
 */

import type { PhotoOriginal } from '../types/photo';

export interface CroppedArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CroppedImageResult {
  blob: Blob;
  base64: string;
  width: number;
  height: number;
}

/**
 * Create an image element from a file or URL
 */
export function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

/**
 * Convert degrees to radians
 */
function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Get the size of a rotated rectangle
 */
function rotateSize(
  width: number,
  height: number,
  rotation: number
): { width: number; height: number } {
  const rotRad = getRadianAngle(rotation);
  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Crop an image based on crop area and rotation
 * Returns a blob and base64 representation of the cropped image
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: CroppedArea,
  rotation = 0,
  outputSize = { width: 400, height: 400 },
  flip = { horizontal: false, vertical: false },
  fillColor = '#000000'
): Promise<CroppedImageResult> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const rotRad = getRadianAngle(rotation);

  // Calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // Set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // Draw rotated image
  ctx.drawImage(image, 0, 0);

  // Extract the cropped area
  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    throw new Error('No 2d context');
  }

  // Set the output size (default 400x400 for profile photos)
  croppedCanvas.width = outputSize.width;
  croppedCanvas.height = outputSize.height;

  // Fill with chosen background color first — handles out-of-bounds crop (zoom-out / bars)
  croppedCtx.fillStyle = fillColor;
  croppedCtx.fillRect(0, 0, outputSize.width, outputSize.height);

  // Clamp source rect to the actual canvas bounds, then map to destination
  const srcX = Math.max(0, pixelCrop.x);
  const srcY = Math.max(0, pixelCrop.y);
  const srcRight = Math.min(canvas.width, pixelCrop.x + pixelCrop.width);
  const srcBottom = Math.min(canvas.height, pixelCrop.y + pixelCrop.height);
  const srcW = srcRight - srcX;
  const srcH = srcBottom - srcY;

  if (srcW > 0 && srcH > 0) {
    const dstX = ((srcX - pixelCrop.x) / pixelCrop.width) * outputSize.width;
    const dstY = ((srcY - pixelCrop.y) / pixelCrop.height) * outputSize.height;
    const dstW = (srcW / pixelCrop.width) * outputSize.width;
    const dstH = (srcH / pixelCrop.height) * outputSize.height;

    croppedCtx.drawImage(canvas, srcX, srcY, srcW, srcH, dstX, dstY, dstW, dstH);
  }

  // Convert to blob
  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }

        // Also get base64
        const base64 = croppedCanvas.toDataURL('image/jpeg', 0.9);

        resolve({
          blob,
          base64,
          width: outputSize.width,
          height: outputSize.height,
        });
      },
      'image/jpeg',
      0.9
    );
  });
}

/**
 * Pad an image with black borders on all sides.
 * Returns a new data URL where the original image is centred on a black canvas.
 * paddingRatio = 0.5 means 50 % padding on each side → output is 2× the original size.
 *
 * Use this before passing images to react-easy-crop so the user can zoom out
 * below zoom=1 and see black bars instead of being locked to the image bounds.
 */
export async function padImageWithBlack(
  src: string,
  paddingRatio = 0.5,
  color = '#000000'
): Promise<string> {
  const img = await createImage(src);
  const padX = Math.round(img.width * paddingRatio);
  const padY = Math.round(img.height * paddingRatio);
  const canvas = document.createElement('canvas');
  canvas.width = img.width + padX * 2;
  canvas.height = img.height + padY * 2;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, padX, padY);
  return canvas.toDataURL('image/jpeg', 0.95);
}

/**
 * Convert a File to base64 data URL
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Convert base64 data URL to Blob
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
}

/**
 * Convert the pre-crop source a photo modal kept in a ref into a PhotoOriginal.
 *
 * Deliberately total: anything it cannot convert becomes `undefined`, meaning "this photo has
 * no recoverable original". Throwing here would abort handleConfirm and stop the operator
 * saving a photo they have already framed — strictly worse than losing the original, which is
 * exactly what happened before originals were kept at all.
 *
 * Pass the UNPADDED source. The cropper renders a padImageWithBlack canvas so the user can zoom
 * out past the image bounds; those black bars are a UI affordance and must never be stored.
 */
export function dataUrlToPhotoOriginal(
  dataUrl: string | null | undefined
): PhotoOriginal | undefined {
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    return undefined;
  }

  try {
    return { blob: dataUrlToBlob(dataUrl), base64: dataUrl };
  } catch (error) {
    console.warn(' [ImageUtils] Could not recover the original photo; uploading the crop only:', error);
    return undefined;
  }
}

/**
 * Pull an already-stored image back out as a data URL.
 *
 * A crop modal opened on an existing photo is handed a URL, not bytes, and the pre-crop
 * source it keeps for `dataUrlToPhotoOriginal` must be a `data:` URL or the original is
 * silently dropped. That is what made a re-crop lose the original saved on the first upload:
 * the row (and both its S3 objects) is deleted and rewritten, so an original that is not
 * re-sent ceases to exist.
 *
 * Deliberately total, like the rest of this path: any failure is `null`, meaning "no original
 * available", never an exception that would abort a save the operator has already framed.
 */
export async function remoteImageToDataUrl(url: string | null | undefined): Promise<string | null> {
  if (!url) return null;
  if (url.startsWith('data:')) return url;

  try {
    // same-origin in production (the app and the image proxy share access.panther.com.br),
    // so the session cookie rides along exactly as it does for an <img> tag.
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return null;
    const blob = await response.blob();
    // An auth redirect answers 200 with an HTML login page; storing that as someone's
    // "original photo" would be worse than having none.
    if (!blob.type.startsWith('image/')) return null;
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Resize an image to a maximum dimension while maintaining aspect ratio
 */
export async function resizeImage(
  imageSrc: string,
  maxDimension: number
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  let { width, height } = image;
  const originalWidth = width;
  const originalHeight = height;

  // Calculate new dimensions
  if (width > height) {
    if (width > maxDimension) {
      height = (height * maxDimension) / width;
      width = maxDimension;
    }
  } else {
    if (height > maxDimension) {
      width = (width * maxDimension) / height;
      height = maxDimension;
    }
  }

  // Logs the actual pixel dimensions, not just an output byte count — a JPEG can shrink in
  // bytes from re-encoding alone without being resized at all, so this is the only log line
  // that proves a resize genuinely happened. Visible in Electron DevTools during device
  // sync, or the browser console during resident self-registration.
  console.log(
    ` [ImageUtils] resizeImage: ${originalWidth}x${originalHeight} -> ${Math.round(width)}x${Math.round(height)} (maxDimension=${maxDimension})`
  );

  canvas.width = width;
  canvas.height = height;

  ctx.drawImage(image, 0, 0, width, height);

  return canvas.toDataURL('image/jpeg', 0.9);
}

/**
 * Downsizes a RAW base64 image string (no "data:image/...;base64," prefix — the format
 * fetchImageAsBase64 and the device-upload payload builders use) to a maximum dimension,
 * returning a raw base64 string in the same prefix-less form.
 *
 * Used before sending a face photo to a physical device (Hikvision/ControlID enrollment):
 * stored profile photos are already 800×800, which is far larger than any facial-recognition
 * device needs for accurate matching and needlessly inflates the enrollment payload,
 * especially over the local networks Electron kiosk stations talk to devices on. Falls back
 * to the original base64 unchanged if resizing fails, so a resize bug never blocks enrollment
 * outright — the device still gets a (larger) usable photo instead of none at all.
 */
export async function resizeBase64Image(base64: string, maxDimension: number): Promise<string> {
  try {
    const resizedDataUrl = await resizeImage(`data:image/jpeg;base64,${base64}`, maxDimension);
    const commaIndex = resizedDataUrl.indexOf(',');
    return commaIndex === -1 ? base64 : resizedDataUrl.substring(commaIndex + 1);
  } catch (error) {
    console.warn(' [ImageUtils] Failed to resize image for device upload, sending original size:', error);
    return base64;
  }
}

/**
 * Get image dimensions from a file
 */
export async function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  const dataUrl = await fileToDataUrl(file);
  const image = await createImage(dataUrl);
  return { width: image.width, height: image.height };
}

// ============================================================================
// Device Photo Sync Functions
// Added for Hikvision/ControlID device photo upload
// ============================================================================

/**
 * Fetch image from URL and convert to base64
 * Includes retry logic for transient network errors (502, 503, timeouts)
 *
 * @param imageUrl - Full URL to the image (e.g., https://access.panther.com.br/api/v1/images/...)
 * @param maxRetries - Maximum number of retry attempts (default: 2)
 * @returns Base64 encoded image data (without data:image prefix)
 */
export async function fetchImageAsBase64(
  imageUrl: string,
  maxRetries: number = 2
): Promise<string> {
  console.log(` [ImageUtils] Fetching image: ${imageUrl}`);

  // Handle data URIs (already base64)
  if (imageUrl.startsWith('data:image')) {
    const commaIndex = imageUrl.indexOf(',');
    if (commaIndex === -1) {
      throw new Error('Invalid data URI format');
    }
    const base64Data = imageUrl.substring(commaIndex + 1);
    console.log(` [ImageUtils] Using embedded data URI, length: ${base64Data.length}`);
    return base64Data;
  }

  let lastError: Error | null = null;

  // Retry logic for network failures
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(` [ImageUtils] Fetch attempt ${attempt + 1}/${maxRetries + 1} for: ${imageUrl}`);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      try {
        const response = await fetch(imageUrl, {
          method: 'GET',
          credentials: 'include', // Include auth cookies if needed
          headers: {
            'Accept': 'image/jpeg,image/png,image/*',
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Handle HTTP errors with smart retry logic
        if (!response.ok) {
          const errorText = await response.text().catch(() => 'No error details');

          // Don't retry on 404 (file not found) or 403 (forbidden) - permanent errors
          if (response.status === 404 || response.status === 403) {
            console.error(` [ImageUtils] Image not found (HTTP ${response.status}): ${imageUrl}`);
            throw new Error(`Image not found (HTTP ${response.status}): ${errorText}`);
          }

          // Retry on 502 (Bad Gateway), 503 (Service Unavailable), 504 (Gateway Timeout)
          if (response.status >= 502 && response.status <= 504) {
            console.warn(` [ImageUtils] Server error HTTP ${response.status}, will retry: ${errorText}`);
            throw new Error(`HTTP ${response.status}: Server temporarily unavailable`);
          }

          // Other errors (4xx, 5xx) - don't retry
          console.error(` [ImageUtils] HTTP ${response.status} error: ${errorText}`);
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        // Get image blob
        const blob = await response.blob();
        console.log(` [ImageUtils]  Downloaded blob: ${blob.size} bytes, type: ${blob.type}`);

        // Verify it's an image
        if (!blob.type.startsWith('image/')) {
          throw new Error(`Invalid content type: ${blob.type}, expected image/*`);
        }

        // Convert blob to base64
        const base64 = await blobToBase64ForDevice(blob);

        // Strip data:image/... prefix if present
        const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

        console.log(' [ImageUtils]  Conversion complete:', {
          originalSize: blob.size,
          base64Length: cleanBase64.length,
          preview: cleanBase64.substring(0, 50) + '...',
        });

        return cleanBase64;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error: any) {
      lastError = error;

      // Check if it's an abort/timeout error
      if (error.name === 'AbortError') {
        console.error(` [ImageUtils] Request timeout (15s) on attempt ${attempt + 1}`);
        lastError = new Error('Request timeout after 15 seconds');
      } else {
        console.error(` [ImageUtils] Fetch attempt ${attempt + 1} failed:`, error.message);
      }

      // Don't retry on 404 or 403
      if (error.message.includes('404') || error.message.includes('403')) {
        break;
      }

      // Wait before retry (exponential backoff: 1s, 2s, 4s)
      if (attempt < maxRetries) {
        const waitTime = Math.pow(2, attempt) * 1000;
        console.log(` [ImageUtils] Waiting ${waitTime}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }

  // All retries failed
  console.error(` [ImageUtils] Failed to fetch image after ${maxRetries + 1} attempts: ${imageUrl}`);
  throw new Error(
    `Failed to fetch image after ${maxRetries + 1} attempts: ${lastError?.message || 'Unknown error'}`
  );
}

/**
 * Convert blob to base64 string (for device upload)
 */
function blobToBase64ForDevice(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Check if a URL is an image URL
 */
export function isImageUrl(url: string): boolean {
  if (!url) return false;

  // Check for common image extensions
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp)$/i;
  if (imageExtensions.test(url)) return true;

  // Check for image API endpoints
  if (url.includes('/images/') || url.includes('/photos/')) return true;

  return false;
}

/**
 * Get full image URL from relative path
 * Handles both absolute URLs and relative paths
 */
export function getFullImageUrl(imageUrl: string, baseUrl?: string): string {
  // If already absolute URL, return as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If base64 data URL, return as-is
  if (imageUrl.startsWith('data:image')) {
    return imageUrl;
  }

  // Use provided base URL or default production URL
  const base = baseUrl || 'https://access.panther.com.br';

  // Ensure proper path joining
  const cleanBase = base.replace(/\/$/, '');
  const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;

  return `${cleanBase}${cleanPath}`;
}
